import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableFooter,
  TableRow,
  TableCell,
  TableContainer,
  FormControlLabel,
  Switch,
  CircularProgress
} from '@material-ui/core';

import { formatNumber, formatNumberWithMonedaSigno } from '../../../assets/shared/formatNumber';
import { texto, importePendiente } from '../textosComprobante';

export const useTablaComprobanteStyles = makeStyles(() => ({
  seccion: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '15px',
    marginBottom: '5px'
  },
  header: {
    fontSize: 'small',
    fontWeight: 'bold',
    backgroundColor: 'rgb(220 220 220)',
    padding: '4px 8px',
    whiteSpace: 'nowrap'
  },
  celda: {
    fontSize: 'small',
    padding: '4px 8px'
  },
  // Cantidades e importes sin cortar (en pantallas chicas la tabla se scrollea).
  numero: {
    whiteSpace: 'nowrap'
  },
  pendiente: {
    backgroundColor: '#fff8d6'
  },
  total: {
    fontSize: 'small',
    fontWeight: 'bold',
    color: 'black',
    padding: '6px 8px'
  }
}));

const COLUMNAS = 8;

/**
 * Renglones de un comprobante con lo pedido, descargado y pendiente (Vnt_Detalle), con el switch "Solo pendientes".
 * En el pie va el total pendiente y lo cobrado: importePago undefined no lo muestra, null es que se está calculando.
 */
const RenglonesComprobanteTable = ({ renglones, importePago, errorImportePago }) => {
  const classes = useTablaComprobanteStyles();
  const [soloPendientes, setSoloPendientes] = useState(false);

  const visibles = renglones.filter((renglon) => !soloPendientes || renglon.cantPendiente > 0);
  const totalPendiente = renglones.reduce((total, renglon) => total + importePendiente(renglon), 0);

  const filaPie = (titulo, valor) => (
    <TableRow>
      <TableCell className={classes.total} colSpan={COLUMNAS - 1} align="right">
        {titulo}
      </TableCell>
      <TableCell className={classes.total} align="right">
        {valor}
      </TableCell>
    </TableRow>
  );

  const valorImportePago = errorImportePago ? (
    <span title={errorImportePago} style={{ color: '#d32f2f' }}>No disponible</span>
  ) : importePago === null ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 'normal' }}>
      <CircularProgress size={12} /> Calculando…
    </span>
  ) : (
    formatNumberWithMonedaSigno(importePago)
  );

  return (
    <React.Fragment>
      <div className={classes.seccion}>
        <Typography variant="subtitle2">Renglones</Typography>
        <FormControlLabel
          label="Solo pendientes"
          control={<Switch size="small" color="primary" checked={soloPendientes} onChange={(event) => setSoloPendientes(event.target.checked)} />}
        />
      </div>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.header}>Artículo</TableCell>
              <TableCell className={classes.header}>Descripción</TableCell>
              <TableCell className={classes.header} align="right">Pedida</TableCell>
              <TableCell className={classes.header} align="right">Descargada</TableCell>
              <TableCell className={classes.header} align="right">Pendiente</TableCell>
              <TableCell className={classes.header} align="right">Precio unit.</TableCell>
              <TableCell className={classes.header} align="right">Total</TableCell>
              <TableCell className={classes.header} align="right">Importe pendiente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibles.length === 0 ? (
              <TableRow>
                <TableCell className={classes.celda} colSpan={COLUMNAS} align="center">
                  {soloPendientes ? 'No tiene renglones pendientes' : 'El comprobante no tiene renglones'}
                </TableCell>
              </TableRow>
            ) : (
              visibles.map((renglon) => (
                <TableRow key={renglon.renglon} className={renglon.cantPendiente > 0 ? classes.pendiente : undefined}>
                  <TableCell className={classes.celda}>{texto(renglon.idArticulo)}</TableCell>
                  <TableCell className={classes.celda}>{texto(renglon.detalle)}</TableCell>
                  <TableCell className={`${classes.celda} ${classes.numero}`} align="right">{formatNumber(renglon.cantidad)}</TableCell>
                  <TableCell className={`${classes.celda} ${classes.numero}`} align="right">{formatNumber(renglon.cantidad - renglon.cantPendiente)}</TableCell>
                  <TableCell className={`${classes.celda} ${classes.numero}`} align="right" style={{ fontWeight: renglon.cantPendiente > 0 ? 'bold' : undefined }}>
                    {formatNumber(renglon.cantPendiente)}
                  </TableCell>
                  <TableCell className={`${classes.celda} ${classes.numero}`} align="right">{formatNumberWithMonedaSigno(renglon.precioImpreso)}</TableCell>
                  <TableCell className={`${classes.celda} ${classes.numero}`} align="right">{formatNumberWithMonedaSigno(renglon.totalImpreso)}</TableCell>
                  <TableCell className={`${classes.celda} ${classes.numero}`} align="right">{formatNumberWithMonedaSigno(importePendiente(renglon))}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            {filaPie('Total pendiente', formatNumberWithMonedaSigno(totalPendiente))}
            {importePago !== undefined && filaPie('Importe pago', valorImportePago)}
          </TableFooter>
        </Table>
      </TableContainer>
    </React.Fragment>
  );
};

export default RenglonesComprobanteTable;
